import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useHistory, useParams } from "react-router-dom";
import { get, patch, post } from "../lib/api";
import { useDebounced, useDocumentTitle, useLoad } from "../lib/hooks";
import { addDays, formatDay } from "../lib/format";
import { applyLink, entityPath, groupMentions, isDate, linkTrigger, noteDates, notePath } from "../lib/notes";
import { formatDate, todayISO } from "../../helpers/datetime";
import { Badge, Button, Empty, ErrorBox, Input, Loading, Menu, MenuItem, Spinner } from "../ui";
import { HiddenLabel } from "../components/Hidden";
import { useShell } from "../shell/context";

const SAVE_DELAY = 800;

const longDate = (ymd) => formatDate(ymd + "T12:00:00Z", { weekday: true });

// Daily notes (E11): one Markdown note per Istanbul date. [[Name]] / @Name
// link a line to a record (stored as mentions, never touches); "- [ ]" on a
// linked line becomes a follow-up. docs/crm/api.md "E11: daily notes".
export default function DailyNotes() {
    const params = useParams();
    const history = useHistory();
    const today = todayISO();
    const date = isDate(params.date) ? params.date : today;
    useDocumentTitle("Daily notes");
    const shell = useShell();
    const { toast, openLog, openCreate } = shell;

    const [note, setNote] = useState(null); // the note as the server last returned it
    const [text, setText] = useState("");
    const [loadError, setLoadError] = useState(null);
    const [status, setStatus] = useState("idle"); // idle | saving | saved | dirty | error
    const [saveError, setSaveError] = useState(null);
    const [warnings, setWarnings] = useState([]);
    const [listTick, setListTick] = useState(0);
    const [trigger, setTrigger] = useState(null);

    const textRef = useRef("");
    const savedRef = useRef(""); // text the server has for dateRef
    const dateRef = useRef(date);
    const loadedRef = useRef(false);
    const taRef = useRef(null);
    const caretRef = useRef(null);

    const { data: list } = useLoad(() => get("v1/crm/notes", { page_size: 60 }), [listTick]);

    // Save: PATCH the note's text (and optional link choices).
    const save = useCallback(
        async ({ forDate, body, quiet } = {}) => {
            const d = forDate || dateRef.current;
            const payload = body || { text: textRef.current };
            const sentText = payload.text !== undefined ? payload.text : textRef.current;
            if (!quiet) setStatus("saving");
            try {
                const res = await patch(`v1/crm/notes/${d}`, payload);
                if (d !== dateRef.current) return res; // left that day meanwhile
                savedRef.current = res.text !== undefined ? res.text : sentText;
                setNote(res);
                setWarnings(res.warnings || []);
                setSaveError(null);
                setStatus(textRef.current === sentText ? "saved" : "dirty");
                setListTick((t) => t + 1);
                return res;
            } catch (e) {
                if (d === dateRef.current) {
                    setStatus("error");
                    setSaveError(e.message);
                } else {
                    toast(`The note of ${formatDay(d)} was not saved: ${e.message}`, { error: true });
                }
                return null;
            }
        },
        [toast]
    );

    // Load the day's note; flush unsaved text of the previous day first.
    useEffect(() => {
        const prev = dateRef.current;
        if (loadedRef.current && prev !== date && textRef.current !== savedRef.current) {
            save({ forDate: prev, body: { text: textRef.current }, quiet: true });
        }
        dateRef.current = date;
        loadedRef.current = false;
        let cancelled = false;
        setNote(null);
        setLoadError(null);
        setWarnings([]);
        setSaveError(null);
        setStatus("idle");
        setTrigger(null);
        get(`v1/crm/notes/${date}`)
            .then((res) => {
                if (cancelled) return;
                setNote(res);
                textRef.current = res.text || "";
                savedRef.current = res.text || "";
                setText(res.text || "");
                loadedRef.current = true;
            })
            .catch((e) => !cancelled && setLoadError(e));
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    // Debounced autosave.
    useEffect(() => {
        if (!loadedRef.current || text === savedRef.current) return undefined;
        setStatus("dirty");
        const t = setTimeout(() => save(), SAVE_DELAY);
        return () => clearTimeout(t);
    }, [text, save]);

    // Flush on leaving the page.
    useEffect(
        () => () => {
            if (loadedRef.current && textRef.current !== savedRef.current) {
                save({ forDate: dateRef.current, body: { text: textRef.current }, quiet: true });
            }
        },
        [save]
    );

    // Put the caret back after inserting a link.
    useEffect(() => {
        if (caretRef.current !== null && taRef.current) {
            const c = caretRef.current;
            caretRef.current = null;
            taRef.current.focus();
            taRef.current.setSelectionRange(c, c);
        }
    }, [text]);

    const reloadNote = async () => {
        try {
            const res = await get(`v1/crm/notes/${dateRef.current}`);
            setNote(res);
            // Server-side edits (a follow-up ticked) change the text; keep any
            // local edits that were not saved yet.
            if (textRef.current === savedRef.current) {
                textRef.current = res.text || "";
                setText(res.text || "");
            }
            savedRef.current = res.text || "";
        } catch (e) {
            toast(e.message, { error: true });
        }
    };

    const onChange = (e) => {
        textRef.current = e.target.value;
        setText(e.target.value);
        setTrigger(linkTrigger(e.target.value, e.target.selectionStart));
    };
    const onCaret = (e) => setTrigger(linkTrigger(e.target.value, e.target.selectionStart));
    const onBlur = () => {
        setTimeout(() => setTrigger(null), 150);
        if (loadedRef.current && textRef.current !== savedRef.current) save();
    };

    const pick = (item) => {
        const caret = taRef.current ? taRef.current.selectionStart : text.length;
        const t = trigger || linkTrigger(text, caret);
        if (!t) return;
        const res = applyLink(text, t, caret, item.title);
        textRef.current = res.text;
        caretRef.current = res.caret;
        setText(res.text);
        setTrigger(null);
    };

    // Link choices: pick a candidate or a just-created record for a name.
    const resolve = (name, type, id) => save({ body: { text: textRef.current, resolve: { [name]: { type, id } } } });

    const createFor = (kind, name) =>
        openCreate(kind, { name, stay: true, onCreated: (rec) => resolve(name, kind, rec.id) });

    const logAsTouch = (m) =>
        openLog({
            [m.entity_type === "company" ? "company" : "contact"]: { id: m.entity_id, name: m.name },
            touch_date: note ? note.date : date,
            summary: m.text,
            mentionId: m.id,
            onLogged: reloadNote,
        });

    const setFollowUp = async (f, done) => {
        // Save pending text first: the server rewrites the line.
        if (textRef.current !== savedRef.current) await save();
        try {
            await post(`v1/crm/notes/follow-ups/${f.id}/done`, { done });
            toast(done ? "Follow-up done." : "Follow-up reopened.");
            await reloadNote();
            setListTick((t) => t + 1);
        } catch (e) {
            toast(e.message, { error: true });
        }
    };

    const go = (d) => history.push(notePath(d));
    const dates = noteDates(list && list.data, today, date);
    const groups = groupMentions(note && note.mentions);
    const unresolved = (note && note.unresolved) || [];
    const followUps = (note && note.follow_ups) || [];

    return (
        <>
            <div className="hz-page-head">
                <div>
                    <div className="hz-crumb">Daily notes</div>
                    <h1 className="hz-page-title">{longDate(date)}</h1>
                </div>
                <div className="hz-row-wrap">
                    <Button iconOnly icon="ri-arrow-left-s-line" title="Previous day" onClick={() => go(addDays(date, -1))} />
                    <Button disabled={date === today} onClick={() => go(today)}>Today</Button>
                    <Button iconOnly icon="ri-arrow-right-s-line" title="Next day" onClick={() => go(addDays(date, 1))} />
                    <Input type="date" small value={date} aria-label="Go to date" onChange={(e) => isDate(e.target.value) && go(e.target.value)} style={{ width: 150 }} />
                </div>
            </div>

            <div className="hz-notes">
                <div className="hz-card hz-notes-dates">
                    {dates.map((n) => (
                        <Link key={n.date} to={notePath(n.date)} className={"hz-notes-date" + (n.date === date ? " active" : "")}>
                            <div className="hz-notes-date-label">
                                {formatDay(n.date, { short: true })}
                                {n.date === today && <span className="hz-xs hz-muted"> · today</span>}
                            </div>
                            <div className="hz-notes-date-sub">{(n.date === date ? (text.split("\n").find((l) => l.trim()) || "") : n.preview) || "Empty"}</div>
                        </Link>
                    ))}
                </div>

                <div className="hz-card hz-notes-editor">
                    {loadError ? (
                        <ErrorBox error={loadError} onRetry={() => go(date)} />
                    ) : note === null ? (
                        <Loading />
                    ) : (
                        <textarea
                            ref={taRef}
                            className="hz-notes-textarea"
                            value={text}
                            onChange={onChange}
                            onKeyUp={onCaret}
                            onClick={onCaret}
                            onBlur={onBlur}
                            onKeyDown={(e) => e.key === "Escape" && setTrigger(null)}
                            placeholder="What happened today? Calls, meetings, ideas…"
                            aria-label="Note text"
                        />
                    )}
                    {trigger && trigger.query.trim().length > 0 && <LinkSuggestions query={trigger.query} onPick={pick} />}
                    {warnings.length > 0 && (
                        <div className="hz-alert hz-alert-warning" style={{ margin: "0 16px 10px" }}>
                            <i className="ri-shield-keyhole-line"></i> {warnings.join(" · ")}: this note may hold sensitive personal data.
                        </div>
                    )}
                    <div className="hz-notes-foot">
                        <span><b>[[Name]]</b> or <b>@Name</b> links a person or company</span>
                        <span><b>- [ ]</b> on a linked line creates a follow-up</span>
                        <span><b>due:2026-10-20</b> sets its date</span>
                        <span className="hz-spacer"></span>
                        <SaveStatus status={status} error={saveError} onRetry={() => save()} />
                    </div>
                </div>

                <div className="hz-notes-side">
                    <div className="hz-card">
                        <div className="hz-card-head">
                            <h2 className="hz-card-title" style={{ fontSize: 14 }}>Linked in this note</h2>
                        </div>
                        {groups.length === 0 && unresolved.length === 0 && <Empty small>No links yet.</Empty>}
                        {groups.map((g) => (
                            <div key={g.key} className="hz-notes-link">
                                <Link to={entityPath(g.entity_type, g.entity_id)} className="hz-row" style={{ gap: 8, color: "var(--hz-text-body)" }}>
                                    <i className={g.entity_type === "company" ? "ri-building-line" : "ri-user-line"} style={{ color: "var(--hz-primary)" }}></i>
                                    <span className="hz-strong hz-spacer hz-ellipsis">{g.name}</span>
                                    <HiddenLabel at={g.hidden_at} />
                                    <i className="ri-arrow-right-s-line hz-muted"></i>
                                </Link>
                                {g.items.map((m) => (
                                    <div key={m.id} className="hz-notes-line">
                                        <span className="hz-notes-line-text" title={`Line ${m.line_no}`}>
                                            {m.is_task && <i className="ri-checkbox-blank-line hz-muted" style={{ marginRight: 4 }}></i>}
                                            {m.text}
                                        </span>
                                        {m.touch_id ? (
                                            <Badge color="success">Logged</Badge>
                                        ) : (
                                            <button type="button" className="hz-btn-link" onClick={() => logAsTouch(m)}>
                                                <i className="ri-chat-new-line"></i> Log as touch
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        ))}
                        {unresolved.length > 0 && (
                            <div className="hz-notes-unresolved">
                                <div className="hz-notes-unresolved-title">Needs a choice</div>
                                {unresolved.map((u) => (
                                    <div key={u.text} className="hz-notes-unresolved-row">
                                        <div className="hz-row" style={{ gap: 6 }}>
                                            <span className="hz-strong hz-spacer hz-ellipsis">{u.text}</span>
                                            <span className="hz-xs hz-muted">{u.reason === "ambiguous" ? "several matches" : "not in CRM yet"}</span>
                                        </div>
                                        <div className="hz-row-wrap" style={{ gap: 6, marginTop: 4 }}>
                                            {(u.candidates || []).length > 0 && (
                                                <Menu
                                                    align="left"
                                                    trigger={(open, toggle) => (
                                                        <Button size="sm" variant="outline" onClick={toggle} icon="ri-links-line">Pick</Button>
                                                    )}
                                                >
                                                    {u.candidates.map((c) => (
                                                        <MenuItem key={c.type + c.id} sub={c.summary} onClick={() => resolve(u.text, c.type, c.id)}>
                                                            {c.name}
                                                        </MenuItem>
                                                    ))}
                                                </Menu>
                                            )}
                                            <Button size="sm" variant="outline" onClick={() => createFor("contact", u.text)}>+ Person</Button>
                                            <Button size="sm" variant="outline" onClick={() => createFor("company", u.text)}>+ Company</Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="hz-card">
                        <div className="hz-card-head">
                            <h2 className="hz-card-title" style={{ fontSize: 14 }}>Follow-ups</h2>
                            <span className="hz-xs hz-muted">from "- [ ]" lines</span>
                        </div>
                        {followUps.length === 0 && <Empty small>No follow-ups in this note.</Empty>}
                        {followUps.map((f) => (
                            <label key={f.id} className="hz-notes-fu">
                                <input type="checkbox" checked={!!f.done} onChange={(e) => setFollowUp(f, e.target.checked)} />
                                <span className="hz-spacer" style={{ minWidth: 0 }}>
                                    <span style={{ textDecoration: f.done ? "line-through" : "none", color: f.done ? "var(--hz-text-muted)" : undefined }}>{f.text}</span>
                                    <span className="hz-small hz-muted" style={{ display: "block" }}>
                                        <Link to={entityPath(f.entity_type, f.entity_id)}>{f.name}</Link>
                                        {" · "}
                                        {f.due_date ? formatDay(f.due_date, { short: true }) : "No date"}
                                    </span>
                                </span>
                            </label>
                        ))}
                    </div>
                </div>
            </div>
        </>
    );
}

function SaveStatus({ status, error, onRetry }) {
    if (status === "saving") return <span className="hz-row" style={{ gap: 6 }}><Spinner /> Saving…</span>;
    if (status === "dirty") return <span>Unsaved changes</span>;
    if (status === "saved") return <span><i className="ri-check-line"></i> Saved</span>;
    if (status === "error")
        return (
            <span className="hz-status-danger">
                Not saved: {error}{" "}
                <button type="button" className="hz-btn-link" onClick={onRetry}>Retry</button>
            </span>
        );
    return <span>Saved automatically as you type</span>;
}

// "Link to" chips: up to 6 people and companies matching what is typed
// after [[ or @ (GET /v1/crm/search).
function LinkSuggestions({ query, onPick }) {
    const dq = useDebounced(query.trim(), 180);
    const [items, setItems] = useState([]);
    useEffect(() => {
        if (!dq) return undefined;
        let cancelled = false;
        get("v1/crm/search", { q: dq, type: "contact,company", limit: 6 })
            .then((res) => !cancelled && setItems(res.data || []))
            .catch(() => !cancelled && setItems([]));
        return () => {
            cancelled = true;
        };
    }, [dq]);
    if (!items.length) return null;
    return (
        <div className="hz-notes-suggest">
            <span className="hz-small hz-muted">Link to</span>
            {items.map((it) => (
                <button
                    key={it.type + it.id}
                    type="button"
                    className="hz-chip hz-chip-sm"
                    title={it.summary}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        onPick(it);
                    }}
                >
                    <i className={it.type === "company" ? "ri-building-line" : "ri-user-line"}></i>
                    {it.title}
                </button>
            ))}
        </div>
    );
}
